import {ActionTree} from "vuex";
import {HdsCollectionMap, StateType} from "@/store/modules/hds/index";
import {RootStateType} from "@/store";
import {DocumentNode} from "graphql";
import {OperationVariables} from "apollo-client/core/types";
import {Entities, Mappings} from "@/store/Database";

/**
 * クエリ用パラメータ
 */
interface QueryParam {
  /** GraphQL クエリ */
  query: DocumentNode;

  /** GraphQL クエリに渡す引数 */
  variables?: OperationVariables;
}

/**
 * ミューテーション用パラメータ
 */
interface MutateParam {
  /** GraphQL ミューテーションクエリ */
  mutation: DocumentNode;

  /** GraphQL クエリに渡す引数 */
  variables?: OperationVariables;

  /** GraphQL クエリに渡す引数ビルダー */
  variablesBuilder?: ((entities: Entities<HdsCollectionMap>) => OperationVariables);
}

/**
 * ロード用パラメータ
 */
interface LoadParam {
  /** ロード前にクライアントデーターベースをクリアする場合 true そうでない場合 false */
  clear?: boolean;

  /** ロード用マッピング */
  mapping: Mappings<HdsCollectionMap>;

  /** 変更を破棄する場合 true そうでない場合 false */
  flushChanges: boolean;

  /** 全てのマッピングにおいて変更記録しない場合 true そうでない場合 false */
  disableChangesApi: boolean;
}

export interface HdsActions {
  gqlQuery: (qp: QueryParam) => Promise<any>;
}

const actions: ActionTree<StateType, RootStateType> = {
  /**
   * データーベースをクリアします
   *
   * @param context
   */
  clear(context) {
    context.state.db.clear();
  },

  /**
   * GraphQL クエリを実行します
   *
   * @param context ActionContext
   * @param query GraphQL クエリ ドキュメントノード
   * @param variables GraphQL クエリに渡す引数
   * @return クエリの実行結果 data が渡される Promise
   */
  gqlQuery(context, {query, variables}: QueryParam) {
    return context.state.gqlClient
        .query({query, variables})
        .then(response => {
          return response.data;
        });
  },

  /**
   * GraphQL クエリを実行し、実行結果をローカルデーターベースにロードします
   *
   * @param context ActionContext
   * @param query GraphQL クエリ ドキュメントノード
   * @param variables GraphQL クエリに渡す引数
   * @param clear ロード前にクライアントデーターベースをクリアする場合 true そうでない場合 false
   * @param mapping ロード用マッピング
   * @param flushChanges 変更を破棄する場合 true そうでない場合 false デフォルトは true
   * @return クエリの実行結果 data が渡される Promise
   */
  gqlQueryAndLoad(context, {query, variables, clear, mapping, flushChanges = true}: QueryParam & LoadParam) {
    if(clear) {
      context.state.db.clear();
    }
    return context
        .dispatch('gqlQuery', {query, variables, clear})
        .then(data => {
          context.commit('load', {data, mapping, flushChanges});
          return data;
        });
  },

  /**
   * GraphQL ミューテーションを実行します
   *
   * @param context ActionContext
   * @param mutation GraphQL ミューテーション ドキュメントノード
   * @param variablesBuilder GraphQL ミューテーションに渡す引数ビルダー
   * @param variables GraphQL ミューテーションに渡す引数
   * @return ミューテーションの実行結果 data が渡される Promise
   */
  gqlMutate(context, {mutation, variablesBuilder, variables}: MutateParam) {
    if(!variables && variablesBuilder) {
      variables = variablesBuilder(context.state.db.entities);
    }
    return context.state.gqlClient
        .mutate({
          mutation,
          variables
        })
        .then(response => {
          return response.data;
        });
  },

  /**
   * GraphQL ミューテーションを実行し、実行結果をローカルデーターベースにロードします
   *
   * @param context ActionContext
   * @param mutation GraphQL ミューテーション ドキュメントノード
   * @param variablesBuilder GraphQL ミューテーションに渡す引数ビルダー
   * @param variables GraphQL ミューテーションに渡す引数
   * @param clear ロード前にクライアントデーターベースをクリアする場合 true そうでない場合 false
   * @param mapping ロード用マッピング
   * @param flushChanges 変更を破棄する場合 true そうでない場合 false デフォルトは false
   * @param disableChangesApi 全てのマッピングにおいて変更記録しない場合 true そうでない場合 false
   * @return ミューテーションの実行結果 data が渡される Promise
   */
  gqlMutateAndLoad(context, {mutation, variablesBuilder, variables, clear, mapping, flushChanges = false, disableChangesApi = true}: MutateParam & LoadParam) {
    if(clear) {
      context.state.db.clear();
    }
    return context
        .dispatch('gqlMutate', {mutation, variablesBuilder, variables})
        .then(data => {
          context.commit('load', {data, mapping, flushChanges, disableChangesApi});
          return data;
        });
  },
};

export default actions;
